import { useState } from 'react'
import { router } from '@inertiajs/react'
import { useFlashToast } from '../../../hooks/useFlashToast'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faFileLines, faPlus, faCircleDot, faHourglassHalf,
  faCircleCheck, faCircleXmark, faChevronRight, faCalendar,
  faLocationDot, faEyeSlash, faIdCard
} from '@fortawesome/free-solid-svg-icons'
import UserSidebar from '../../../components/user/Sidebar'
import UserTopbar from '../../../components/user/Topbar'

const STATUS_CONFIG = {
  menunggu:  { label: 'Menunggu',        icon: faCircleDot,     bg: 'bg-gray-100',    text: 'text-gray-600',    ring: 'ring-gray-200'   },
  diproses:  { label: 'Sedang Diproses', icon: faHourglassHalf, bg: 'bg-amber-50',    text: 'text-amber-700',   ring: 'ring-amber-200'  },
  selesai:   { label: 'Selesai',         icon: faCircleCheck,   bg: 'bg-emerald-50',  text: 'text-emerald-700', ring: 'ring-emerald-200' },
  ditolak:   { label: 'Ditolak',         icon: faCircleXmark,   bg: 'bg-red-50',      text: 'text-red-600',     ring: 'ring-red-200'    },
}

const CATEGORY_EMOJI = {
  'Korupsi & Gratifikasi':      '🏛️',
  'Penyalahgunaan Wewenang':    '⚖️',
  'Konflik Kepentingan':        '🔄',
  'Penggelapan Aset Negara':    '📦',
  'Suap & Pungli':              '💰',
  'Pemalsuan Dokumen':          '📄',
  'Pelanggaran Kode Etik':      '📋',
  'Kecurangan Pengadaan':       '🛒',
}

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.menunggu
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ring-1 ${cfg.bg} ${cfg.text} ${cfg.ring}`}>
      <FontAwesomeIcon icon={cfg.icon} className="w-3 h-3" />
      {cfg.label}
    </span>
  )
}

export default function UserReportsIndex({ current_user, reports }) {
  useFlashToast()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const stats = {
    total:    reports.length,
    menunggu: reports.filter(r => r.status === 'menunggu').length,
    diproses: reports.filter(r => r.status === 'diproses').length,
    selesai:  reports.filter(r => r.status === 'selesai').length,
  }

  return (
    <div className="flex h-screen bg-gray-50 font-sans overflow-hidden">
      <UserSidebar current_user={current_user} open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <UserTopbar current_user={current_user} onMenuToggle={() => setSidebarOpen(true)} />

        <main className="flex-1 overflow-y-auto p-4 lg:p-6">

          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div>
              <h1 className="text-xl font-bold text-gray-900">Laporan Saya</h1>
              <p className="text-sm text-gray-500 mt-0.5">Daftar laporan yang telah Anda kirimkan</p>
            </div>
            <a href="/user/reports/new"
              className="flex items-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm shadow-green-200">
              <FontAwesomeIcon icon={faPlus} className="w-3.5 h-3.5" />
              Buat Laporan
            </a>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            {[
              { label: 'Total Laporan',   value: stats.total,    color: 'blue'    },
              { label: 'Menunggu',        value: stats.menunggu, color: 'gray'    },
              { label: 'Sedang Diproses', value: stats.diproses, color: 'amber'   },
              { label: 'Selesai',         value: stats.selesai,  color: 'emerald' },
            ].map(s => (
              <div key={s.label} className="bg-white rounded-xl border border-gray-200 px-4 py-3 flex items-center gap-3">
                <div className={`w-2 h-8 rounded-full ${{
                  blue: 'bg-blue-400', gray: 'bg-gray-300', amber: 'bg-amber-400', emerald: 'bg-emerald-400'
                }[s.color]}`} />
                <div>
                  <div className="text-2xl font-bold text-gray-900">{s.value}</div>
                  <div className="text-[11px] text-gray-400">{s.label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Reports list */}
          {reports.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 flex flex-col items-center justify-center py-20 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <FontAwesomeIcon icon={faFileLines} className="w-7 h-7 text-gray-300" />
              </div>
              <p className="text-sm font-semibold text-gray-500">Belum ada laporan</p>
              <p className="text-xs text-gray-400 mt-1 mb-5">Laporan yang Anda kirim akan muncul di sini</p>
              <a href="/user/reports/new"
                className="flex items-center gap-2 px-5 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-lg transition-colors">
                <FontAwesomeIcon icon={faPlus} className="w-3.5 h-3.5" />
                Buat Laporan Pertama
              </a>
            </div>
          ) : (
            <div className="space-y-3">
              {reports.map(report => (
                <a key={report.id} href={`/user/reports/${report.id}`}
                  className="block bg-white rounded-xl border border-gray-200 hover:border-green-300 hover:shadow-sm transition-all p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      {/* Category emoji */}
                      <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center flex-shrink-0 text-lg">
                        {CATEGORY_EMOJI[report.category] || '📋'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="text-[11px] font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded-md">
                            {report.category}
                          </span>
                          {report.anonymous && (
                            <span className="flex items-center gap-1 text-[11px] text-gray-400">
                              <FontAwesomeIcon icon={faEyeSlash} className="w-2.5 h-2.5" /> Anonim
                            </span>
                          )}
                        </div>
                        <p className="text-sm font-semibold text-gray-900 truncate">{report.title}</p>
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{report.description}</p>
                        <div className="flex items-center gap-3 mt-2 text-[11px] text-gray-400">
                          <span className="font-mono font-medium text-gray-500">{report.tracking_code}</span>
                          {report.location && (
                            <span className="flex items-center gap-1">
                              <FontAwesomeIcon icon={faLocationDot} className="w-2.5 h-2.5" />
                              {report.location}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <FontAwesomeIcon icon={faCalendar} className="w-2.5 h-2.5" />
                            {report.created_at}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      {report.identity_reveal_requested && !report.identity_reveal_approved && (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ring-1 bg-amber-50 text-amber-700 ring-amber-200 animate-pulse">
                          <FontAwesomeIcon icon={faIdCard} className="w-3 h-3" />
                          Minta Identitas
                        </span>
                      )}
                      <StatusBadge status={report.status} />
                      <FontAwesomeIcon icon={faChevronRight} className="w-3.5 h-3.5 text-gray-300" />
                    </div>
                  </div>
                </a>
              ))}
            </div>
          )}

        </main>
      </div>
    </div>
  )
}
